import React, { useEffect, useRef, useState } from 'react';
import { getAuth } from "firebase/auth";
import { loadStripe } from '@stripe/stripe-js';
import './QuotaModal.css';
import { stripeConfig, environment } from './config/stripe.config';
import { functionUrls } from './services/firebaseFunctions';
import playBillingService from './services/playBilling';
import { isTWA, isPlayBillingAvailable, getPaymentMethod } from './utils/twaDetection';

export default function QuotaModal({ onClose }) {
  const containerRef = useRef(null);
  const checkoutRef = useRef(null);
  const stripeRef = useRef(null);
  const clientSecretRef = useRef(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [pendingMsg, setPendingMsg] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [showFallback, setShowFallback] = useState(false);
  const [referrer, setReferrer] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('stripe');
  const [stripeReady, setStripeReady] = useState(false);

  useEffect(() => {
    setIsVisible(true);

    // Extract referrer from URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const referrerParam = urlParams.get('r');
    if (referrerParam) {
      setReferrer(referrerParam);
    }

    // Detect payment method and preload if needed
    (async () => {
      const detectedMethod = await getPaymentMethod();
      setPaymentMethod(detectedMethod);

      if (detectedMethod === 'stripe') {
        try {
          const stripe = await loadStripe(stripeConfig.publishableKey, { advancedFraudSignals: false });
          stripeRef.current = stripe;

          const { clientSecret } = await createCheckoutSession();
          clientSecretRef.current = clientSecret;

          setStripeReady(true);
        } catch (err) {
          console.error('Stripe preload error:', err);
          setShowFallback(true);
        }
      }
    })();
  }, []);

  useEffect(() => {
    if (showCheckout && !showFallback && stripeReady && clientSecretRef.current) {
      (async () => {
        try {
          if (checkoutRef.current) {
            checkoutRef.current.destroy();
            checkoutRef.current = null;
          }

          const checkout = await stripeRef.current.initEmbeddedCheckout({ clientSecret: clientSecretRef.current });
          checkoutRef.current = checkout;
          checkout.mount(containerRef.current);
        } catch (err) {
          console.error('Checkout mount error:', err);
          setErrorMsg(`Failed to display payment form: ${err.message}`);
          setShowFallback(true);
        }
      })();
    }

    return () => {
      if (checkoutRef.current) {
        checkoutRef.current.destroy();
        checkoutRef.current = null;
      }
    };
  }, [showCheckout, showFallback, stripeReady]);

  const createCheckoutSession = async () => {
    const auth = getAuth();
    const idToken = auth.currentUser ? await auth.currentUser.getIdToken().catch(() => null) : null;

    const headers = { 'Content-Type': 'application/json' };
    if (idToken) headers.Authorization = `Bearer ${idToken}`;

    const body = {
      origin: window.location.origin,
      hostname: window.location.hostname,
      ...(referrer && { referrer }),
    };

    const abortController = new AbortController();
    const timeoutId = setTimeout(() => abortController.abort(), 12000);

    try {
      const resp = await fetch(functionUrls.createCheckoutSession, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
        signal: abortController.signal,
      });

      if (!resp.ok) {
        const errorData = await resp.text().catch(() => 'Unknown error');
        throw new Error(`Checkout session error (HTTP ${resp.status}): ${errorData}`);
      }

      const { clientSecret, url } = await resp.json();
      if (!clientSecret && !url) throw new Error('Server did not return clientSecret or url');

      return { clientSecret, url };
    } catch (err) {
      if (err.name === 'AbortError') throw new Error('Checkout request timed out.');
      throw err;
    } finally {
      clearTimeout(timeoutId);
    }
  };

  const handleBuyClick = async () => {
    setErrorMsg('');
    setPendingMsg('');
    setIsLoading(true);

    try {
      if (paymentMethod === 'play-billing') {
        const purchaseData = await playBillingService.initiatePurchase('curio_creditsot_150');
        const verification = await playBillingService.verifyPurchase(purchaseData);

        // Handle pending payment status
        if (verification.status === 'pending') {
          setPendingMsg('Payment is pending. Your purchase will be unlocked once the payment provider processes it. This usually takes a few minutes.');
          // Don't close the modal, but stop loading
          return;
        }

        if (verification.success) {
          onClose();
          return;
        }
        throw new Error('Purchase verification failed');
      } else {
        if (showFallback || !stripeReady) {
          const { url } = await createCheckoutSession();
          if (url) window.location.href = url;
          else throw new Error('No checkout URL received');
        } else {
          setShowCheckout(true);
        }
      }
    } catch (err) {
      console.error('Purchase error:', err);
      setErrorMsg(err.message || 'Purchase failed. Please try again.');
      if (paymentMethod === 'stripe') setShowFallback(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToFeatures = () => {
    setShowCheckout(false);
    setShowFallback(false);
    setErrorMsg('');
    if (checkoutRef.current) {
      checkoutRef.current.destroy();
      checkoutRef.current = null;
    }
  };

  const handleClose = () => {
    setShowCheckout(false);
    setShowFallback(false);
    setErrorMsg('');
    onClose();
  };

  return (
    <div className={`quota-modal-backdrop ${isVisible ? 'visible' : ''}`} role="dialog" aria-modal="true">
      <div className={`quota-modal-card ${isVisible ? 'visible' : ''} ${showCheckout ? 'checkout-active' : ''}`}>
        {!showCheckout ? (
          <div className="product-content">
            <button className="close-button" onClick={handleClose} aria-label="Close purchase dialog">
              <span className="material-icons">close</span>
            </button>

            <img src="/CurioLogo.png" alt="CURIO App Logo" className="owl-image" />

            <div className="features-container">
              <div className="feature-item">
                <span className="material-icons feature-icon">explore</span>
                <div className="feature-content">
                  <h3 className="feature-title">Effortless Exploration</h3>
                  <p className="feature-description">Instantly access detailed audio guides with just a tap — no typing or prompting required.</p>
                </div>
              </div>
              <div className="feature-item">
                <span className="material-icons feature-icon">auto_stories</span>
                <div className="feature-content">
                  <h3 className="feature-title">Make your journey more meaningful</h3>
                  <p className="feature-description">appreciate and understand the places you visit better.</p>
                </div>
              </div>
              <div className="feature-item">
                <span className="material-icons feature-icon">travel_explore</span>
                <div className="feature-content">
                  <h3 className="feature-title">Millions of Points of Interest</h3>
                  <p className="feature-description">Discover fascinating insights on cities, landmarks, hidden gems, and more, wherever you go.</p>
                </div>
              </div>
              <div className="feature-item">
                <span className="material-icons feature-icon">volume_up</span>
                <div className="feature-content">
                  <h3 className="feature-title">2.5 hours of audio guide included.</h3>
                  <p className="feature-description">(150 AI Credits)</p>
                </div>
              </div>
            </div>

            {errorMsg && (
              <div className="quota-error" style={{ marginBottom: '1rem' }}>
                {errorMsg}
              </div>
            )}

            {pendingMsg && (
              <div style={{
                padding: '1rem',
                backgroundColor: '#fff3cd',
                border: '1px solid #ffc107',
                borderRadius: '8px',
                marginBottom: '1rem',
                color: '#856404'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span className="material-icons" style={{ color: '#ffc107' }}>schedule</span>
                  <span>{pendingMsg}</span>
                </div>
              </div>
            )}

            <button
              className="purchase-button"
              onClick={handleBuyClick}
              disabled={isLoading}
            >
              <div className="button-left">
                <span className="button-main-text">{isLoading ? 'Loading...' : 'Start Exploring'}</span>
              </div>
              <div className="button-right">
                <span className="button-price">$9.95</span>
                <span className="button-onetime">onetime</span>
              </div>
            </button>

            {paymentMethod === 'play-billing' && (
              <p style={{ fontSize: '12px', color: '#666', marginTop: '10px', textAlign: 'center' }}>
              </p>
            )}
          </div>
        ) : (
          <div className="checkout-content">
            <button className="close-button" onClick={handleBackToFeatures} aria-label="Back">
              &larr;
            </button>
            <h2 className="checkout-title">Complete Your Purchase</h2>

            {environment === 'development' && (
              <div className="dev-notice">
                <strong>⚠️ Development Mode:</strong> {paymentMethod === 'play-billing' ? 'Play Billing Test Mode' : 'Test card: 4242...'}
              </div>
            )}

            {errorMsg && <div className="quota-error">{errorMsg}</div>}

            {showFallback ? (
              <div className="fallback-checkout">
                <p>Use our secure payment page:</p>
                <button
                  className="fallback-button"
                  onClick={async () => {
                    setIsLoading(true);
                    try {
                      const { url } = await createCheckoutSession();
                      if (url) window.location.href = url;
                    } catch (err) {
                      setErrorMsg(err.message || 'Unable to initiate payment.');
                    } finally {
                      setIsLoading(false);
                    }
                  }}
                  disabled={isLoading}
                >
                  {isLoading ? 'Opening...' : 'Pay with Stripe →'}
                </button>
              </div>
            ) : (
              <div
                ref={containerRef}
                className="stripe-checkout-container"
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}